// Admin Undo per-surface affordance.
//
// Renders a compact "Admin: Undo {Label}" link/button next to a
// completed-state indicator, gated on the
// `DealFlow.Admin.Undo` permission. Clicking opens the shared
// AdminUndoConfirmModal; confirming with a valid reason fires the
// caller-supplied mutation. Success copy is also caller-supplied so
// each surface can phrase its own confirmation.
//
// This component is intentionally generic so every Admin Undo
// surface (Section 9 hand-off, S8 certs, DealOutcome finalize,
// LaneAssessment ISSUED chip, criteria-item terminal chip, analyst
// Section 2 and Closing Step 1 certifications, escrow issuance and
// funding verification) uses the same wiring.

import { useState, type ReactNode } from "react";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { AdminUndoConfirmModal } from "./AdminUndoConfirmModal";

export interface AdminUndoButtonProps {
  /** Short label that follows "Admin: Undo " in the affordance, e.g.
   *  "Hand-off", "Sign", "Finalize", "Issuance", "Certify". */
  label: string;
  /** Title shown in the confirmation modal. */
  modalTitle: string;
  /** Action-specific consequence copy shown in the modal body. */
  modalConsequence: ReactNode;
  /** Toast copy on success. */
  successToast: string;
  /** The mutation invoker — receives the trimmed reason. Any error
   *  thrown / rejected is surfaced via the global toast helper. */
  invoke: (reason: string) => Promise<unknown>;
  /** Inline kebab class override for placement next to a chip. */
  className?: string;
}

export function AdminUndoButton(props: AdminUndoButtonProps) {
  const auth = useOptionalAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  // No <AuthProvider> in the tree (typically tests that didn't bootstrap
  // auth) → render nothing. The forward action's UI still works; only
  // the optional admin affordance hides.
  if (!auth?.hasPermission(DEAL_FLOW_PERMISSIONS.AdminUndo)) {
    return null;
  }

  const onConfirm = async (reason: string) => {
    setPending(true);
    try {
      await props.invoke(reason);
      toast.success(props.successToast);
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={
          props.className ??
          "inline-flex items-center gap-space-1 text-caption font-display uppercase tracking-label text-status-error hover:underline underline-offset-2"
        }
        onClick={() => setOpen(true)}
        data-testid="admin-undo-button"
        aria-label={`Admin undo: ${props.label}`}
      >
        Admin: Undo {props.label}
      </button>
      <AdminUndoConfirmModal
        open={open}
        onClose={() => (pending ? undefined : setOpen(false))}
        actionTitle={props.modalTitle}
        actionConsequence={props.modalConsequence}
        onConfirm={onConfirm}
        isPending={pending}
      />
    </>
  );
}
